/**
 * EditTransactionModal Component
 * Modal for editing transaction details before import
 */
import { useState, useEffect, useMemo, type FormEvent } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import CategorySelect from '@/components/transactions/CategorySelect';
import { formatCurrency } from '@/utils/format';
import { useUpdateObservation } from '@/hooks/useDocuments';
import { useCategoryById } from '@/hooks/useCategories';
import { ApiClientError } from '@/services/api';
import type { TransactionObservation } from '@/types/document';
import type { CategoryType } from '@/types';

export interface EditTransactionModalProps {
  isOpen: boolean;
  observation: TransactionObservation | null;
  stagedImportId: string;
  onClose: () => void;
  onSaved: () => void;
}

interface FormData {
  date: string;
  amount: string;
  description: string;
  categoryId: string | undefined;
}

export function EditTransactionModal({
  isOpen,
  observation,
  stagedImportId,
  onClose,
  onSaved,
}: EditTransactionModalProps) {
  const [formData, setFormData] = useState<FormData>({
    date: '',
    amount: '',
    description: '',
    categoryId: undefined,
  });
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateObservation();

  // Get original category info for display
  const originalCategory = useCategoryById(observation?.observedCategory ?? undefined);

  /**
   * Determine category type from the current form amount for filtering.
   * Uses the form amount (which user may have edited) to show appropriate categories.
   * - Positive amounts show income categories
   * - Negative amounts show expense categories
   * - Zero or invalid shows all categories
   */
  const categoryType: CategoryType | undefined = useMemo(() => {
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount === 0) return undefined;
    return amount >= 0 ? 'income' : 'expense';
  }, [formData.amount]);

  // Initialize form when observation changes
  useEffect(() => {
    if (observation) {
      setFormData({
        date: observation.observedDate.split('T')[0] ?? observation.observedDate,
        amount: observation.observedAmount.toString(),
        description: observation.observedDescription,
        categoryId: observation.observedCategory ?? undefined,
      });
      setError(null);
    }
  }, [observation]);

  const handleClose = () => {
    setFormData({ date: '', amount: '', description: '', categoryId: undefined });
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!observation) return;

    setError(null);

    // Validate
    if (!formData.date) {
      setError('Date is required');
      return;
    }
    if (!formData.amount || isNaN(parseFloat(formData.amount))) {
      setError('Amount must be a valid number');
      return;
    }
    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }

    // Check if anything changed
    const originalDate = observation.observedDate.split('T')[0] ?? observation.observedDate;
    const dateChanged = formData.date !== originalDate;
    const amountChanged = parseFloat(formData.amount) !== observation.observedAmount;
    const descriptionChanged = formData.description.trim() !== observation.observedDescription;
    const categoryChanged = formData.categoryId !== (observation.observedCategory ?? undefined);

    if (!dateChanged && !amountChanged && !descriptionChanged && !categoryChanged) {
      // Nothing changed, just close
      handleClose();
      return;
    }

    try {
      await updateMutation.mutateAsync({
        observationId: observation.id,
        request: {
          observedDate: dateChanged ? formData.date : undefined,
          observedAmount: amountChanged ? parseFloat(formData.amount) : undefined,
          observedDescription: descriptionChanged ? formData.description.trim() : undefined,
          categoryId: categoryChanged ? formData.categoryId : undefined,
        },
        stagedImportId,
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to update transaction. Please try again.');
      }
    }
  };

  if (!isOpen || !observation) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-transaction-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h2
          id="edit-transaction-modal-title"
          className="text-lg font-semibold text-gray-900 mb-4"
        >
          Edit Transaction
        </h2>

        <p className="text-sm text-gray-600 mb-4">
          Make corrections before importing this transaction.
        </p>

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            max={new Date().toISOString().split('T')[0]}
          />

          <div>
            <Input
              label="Amount"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              hint="Use negative for expenses, positive for income"
            />
            {formData.amount && !isNaN(parseFloat(formData.amount)) && (
              <p className="mt-1 text-sm text-gray-500">
                Preview: {formatCurrency(parseFloat(formData.amount))}
              </p>
            )}
          </div>

          <Input
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Transaction description"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <CategorySelect
              value={formData.categoryId}
              onChange={(categoryId) => setFormData({ ...formData, categoryId })}
              placeholder="Select a category (optional)"
              allowClear={true}
              categoryType={categoryType}
            />
          </div>

          {/* Original values for reference */}
          <div className="p-3 bg-gray-50 rounded-lg text-sm">
            <p className="font-medium text-gray-700 mb-2">Original values:</p>
            <dl className="space-y-1 text-gray-600">
              <div className="flex justify-between">
                <dt>Date:</dt>
                <dd>{observation.observedDate.split('T')[0]}</dd>
              </div>
              <div className="flex justify-between">
                <dt>Amount:</dt>
                <dd>{formatCurrency(observation.observedAmount)}</dd>
              </div>
              <div>
                <dt>Description:</dt>
                <dd className="mt-0.5">{observation.observedDescription}</dd>
              </div>
              {originalCategory && (
                <div className="flex justify-between">
                  <dt>Category:</dt>
                  <dd>{originalCategory.name}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={updateMutation.isPending}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending} className="flex-1">
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditTransactionModal;
